import React from 'react';
import { systemText } from '../../helpers/string';
import './debugPanel.css';

// The Alt+I debug panel for bug reports, shared by the portal and partners (E4-2443). Each app
// keeps a thin components/debugHelper wrapper that reads its own store and passes the props in:
// e4shared can't import an app's config, package.json or actions. It is also the only place the
// running build is shown; nothing on the page itself names it.

// The running build: `v2026.10.15 (build 482)` from /runtime-config.js's `build`, which the
// deploy writes; `dev` under `npm start`; otherwise (the ADO builds) the app's package.json version.
export const appVersion = (build, nodeEnv, packageVersion) => {
	if (build?.version) return build.number ? `v${build.version} (build ${build.number})` : `v${build.version}`;
	return nodeEnv === 'development' ? 'dev' : packageVersion;
};

// Bootstrap 3's breakpoints, which the portal's layouts are built on.
export const breakpoint = width => (width < 768 ? 'XS' : width < 992 ? 'SM' : width < 1200 ? 'MD' : 'LG');

const hasValue = value => value !== undefined && value !== null && value !== '';

// A translation, or `fallback` when it isn't loaded (systemText returns the path itself).
const translated = (path, fallback) => {
	const text = systemText(path);
	return text === path ? fallback : text;
};

// A long value (page, user agent) on one line with an ellipsis; a click shows all of it.
const Clamp = ({ text }) => {
	const [open, setOpen] = React.useState(false);
	return (
		<button
			type="button"
			className={`debug-info-clamp${open ? ' open' : ''}`}
			title={open ? 'Click to shorten' : text}
			aria-expanded={open}
			onClick={() => setOpen(!open)}
		>
			{text}
		</button>
	);
};

const Row = ({ label, value, display, mono, clamp, title }) => (
	<>
		<dt>{label}</dt>
		<dd className={mono ? 'debug-info-mono' : undefined} title={title}>
			{clamp ? <Clamp text={String(value)} /> : display ?? String(value)}
		</dd>
	</>
);

// The API section's rows: the deployed API's version from GET api-version (the same shape as
// `build`), `dev` for a local API with no stamped version, or one row saying why there is none.
const apiRows = api => {
	if (!api) return [];
	if (api.status === 'loading') return [{ key: 'api-version', label: 'Version', value: 'loading…' }];
	if (api.status !== 'done') return [{ key: 'api-version', label: 'Version', value: 'unavailable' }];
	const { version, number, sha } = api.data || {};
	return [
		{ key: 'api-version', label: 'Version', value: version || 'dev', mono: true },
		{ key: 'api-build', label: 'Build', value: number, mono: true },
		{ key: 'api-commit', label: 'Commit', value: sha, display: sha?.slice(0, 7), title: sha, mono: true },
	];
};

// Each row has a `key`, the name it has in the copied text (the portal panel's names, so
// reports read as they always have), a `label` on screen, and `value`; `display` overrides
// what is shown. Rows without a value are left out, so an app shows only what its user has.
// The first section is the site's own build, titled with the app's name; without a deploy's
// `build` it shows only `dev` or the package.json version.
const buildSections = ({ appName, user, build, fallbackVersion, api, componentInfo, now }) => {
	const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
	return [
		{
			title: appName,
			rows: [
				{ key: 'build-version', label: 'Version', value: build?.version || fallbackVersion, mono: true },
				{ key: 'build-number', label: 'Build', value: build?.number, mono: true },
				{ key: 'app-commit', label: 'Commit', value: build?.sha, display: build?.sha?.slice(0, 7), title: build?.sha, mono: true },
			],
		},
		{ title: 'API', rows: apiRows(api) },
		{
			title: 'User',
			rows: [
				{ key: 'user-fullname', label: 'Name', value: user.full_Name },
				{ key: 'user-email', label: 'Email', value: user.email },
				{ key: 'user-id', label: 'User id', value: user.id, mono: true },
				{ key: 'user-posthogid', label: 'PostHog id', value: user.unique_Id, mono: true, clamp: true },
				{ key: 'user-language', label: 'Language', value: user.language_Code },
				{ key: 'user-authority', label: 'Authority', value: user.authority },
				{ key: 'partner-id', label: 'Partner id', value: user.partner_Id, mono: true },
			],
		},
		{
			title: 'Company',
			rows: [
				{ key: 'company-name', label: 'Name', value: user.companySettings?.name },
				{ key: 'company-id', label: 'Company id', value: user.company_Id, mono: true },
				{ key: 'IsLearn2', label: 'Learn2', value: user.companySettings?.isLearn2?.toString() },
			],
		},
		{
			title: 'Component',
			rows: componentInfo.map(info => ({ key: info.key, label: info.key, value: info.value })),
		},
		{
			title: 'Session',
			rows: [
				{ key: 'current-url', label: 'Page', value: window.location.href, mono: true, clamp: true },
				{ key: 'viewport-size', label: 'Viewport', value: `${window.innerWidth}x${window.innerHeight}`, display: `${window.innerWidth} × ${window.innerHeight}` },
				{ key: 'screen-size', label: 'Breakpoint', value: breakpoint(window.innerWidth) },
				{ key: 'timestamp', label: 'Time', value: now.toISOString(), display: now.toLocaleString() },
				{ key: 'time zone', label: 'Time zone', value: timeZone },
				{ key: 'browser', label: 'Browser', value: navigator.userAgent, clamp: true },
			],
		},
	]
		.map(section => ({ ...section, rows: section.rows.filter(row => hasValue(row.value)) }))
		.filter(section => section.rows.length);
};

// `app-version: …`, then one block per section: `User:`, a blank line, `key: value` lines.
export const formatDebugInfo = (version, sections) =>
	[
		`app-version: ${version}`,
		...sections.map(section => [`${section.title}:`, '', ...section.rows.map(row => `${row.key}: ${row.value}`)].join('\n')),
	].join('\n\n');

// Props:
//   appName               titles the site's own build section: `Portal` or `Partners`
//   user                  the signed-in profile (both apps get the same Profile from the API)
//   build                 /runtime-config.js's `build` ({ version, number, sha }), if any
//   packageVersion        the app's package.json version, shown when there is no build
//   nodeEnv               defaults to process.env.NODE_ENV
//   canView               false hides the panel (the portal shows it to partners and spoofers only)
//   onReloadTranslations  shows the Reload translations action; may return a promise
//   translationsLoading   true while the app has no translations yet
//   loadApiVersion        shows the API section; returns a promise of the API's { version, number, sha }.
//                         Called when the panel opens (not before), and again on the next open after a failure
export const DebugPanel = ({
	appName = 'App',
	user,
	build,
	packageVersion,
	nodeEnv = process.env.NODE_ENV,
	canView = true,
	onReloadTranslations,
	translationsLoading = false,
	loadApiVersion,
}) => {
	const [show, setShow] = React.useState(false);
	const [copied, setCopied] = React.useState(false);
	const [reloading, setReloading] = React.useState(false);
	const [componentInfo, setComponentInfo] = React.useState([]);
	const [api, setApi] = React.useState(loadApiVersion ? { status: 'idle' } : null);
	// The latest loader and request state, so a new function from the app or a re-render while
	// open never fires a second request.
	const loadApi = React.useRef(loadApiVersion);
	loadApi.current = loadApiVersion;
	const apiStatus = React.useRef('idle');
	const outer = React.useRef(null);
	const mounted = React.useRef(true);

	React.useEffect(() => {
		mounted.current = true;
		const onKeyDown = e => {
			if (e.altKey && e.code === 'KeyI') setShow(open => !open);
			if (e.key === 'Escape') setShow(false);
		};
		const onMouseDown = e => {
			if (!outer.current?.contains(e.target)) setShow(false);
		};
		document.addEventListener('keydown', onKeyDown);
		document.addEventListener('mousedown', onMouseDown);
		return () => {
			mounted.current = false;
			document.removeEventListener('keydown', onKeyDown);
			document.removeEventListener('mousedown', onMouseDown);
		};
	}, []);

	// Other components add facts with e4shared's DebugInfo(key, value), which fires this event;
	// a blank value removes the key.
	React.useEffect(() => {
		const onAddDebugInfo = e => {
			const { key, value } = e.detail || {};
			setComponentInfo(prev => {
				const rest = prev.filter(info => info.key !== key);
				return hasValue(value) && String(value).trim().length ? [...rest, { key, value }] : rest;
			});
		};
		window.addEventListener('addDebugInfo', onAddDebugInfo);
		return () => window.removeEventListener('addDebugInfo', onAddDebugInfo);
	}, []);

	React.useEffect(() => {
		if (!show || !loadApi.current || apiStatus.current === 'loading' || apiStatus.current === 'done') return;
		const update = next => {
			apiStatus.current = next.status;
			if (mounted.current) setApi(next);
		};
		update({ status: 'loading' });
		Promise.resolve()
			.then(loadApi.current)
			.then(
				data => update({ status: 'done', data }),
				() => update({ status: 'error' }),
			);
	}, [show]);

	React.useEffect(() => {
		if (!copied) return undefined;
		const timer = setTimeout(() => setCopied(false), 1500);
		return () => clearTimeout(timer);
	}, [copied]);

	if (!user || !canView) return null;

	const version = appVersion(build, nodeEnv, packageVersion);
	const fallbackVersion = appVersion(undefined, nodeEnv, packageVersion);
	const sections = show ? buildSections({ appName, user, build, fallbackVersion, api, componentInfo, now: new Date() }) : [];

	const copy = async () => {
		try {
			await navigator.clipboard.writeText(formatDebugInfo(version, sections));
			setCopied(true);
		} catch (err) {
			console.error('Failed to copy debug info', err);
		}
	};

	const reloadTranslations = () => {
		setReloading(true);
		Promise.resolve(onReloadTranslations()).finally(() => {
			if (mounted.current) setReloading(false);
		});
	};
	const loading = reloading || translationsLoading;

	return (
		<div className="aside-debug-info" ref={outer}>
			{show && (
				<div className="debug-info-open" role="dialog" aria-label="Debug info">
					<div className="debug-info-header">
						<div className="debug-info-title">Debug info</div>
						<button
							type="button"
							className="debug-info-copy"
							onClick={copy}
							data-testid="button-copy-info"
							title={translated('General/DebugInfo/Actions/CopyInfo', 'Copy debug info')}
						>
							<i className={`fa ${copied ? 'fa-check' : 'fa-copy'}`} aria-hidden="true" />
							{copied ? 'Copied' : 'Copy'}
						</button>
					</div>

					{sections.map(section => (
						<section key={section.title}>
							<h4>{section.title}</h4>
							<dl>
								{section.rows.map(row => (
									<Row key={row.key} {...row} />
								))}
							</dl>
						</section>
					))}

					{onReloadTranslations && (
						<section>
							<h4>Actions</h4>
							<button
								type="button"
								className="debug-info-action"
								onClick={reloadTranslations}
								disabled={loading}
								data-testid="button-reload-translations"
							>
								{loading && <i className="fa fa-spinner fa-spin" aria-hidden="true" />}
								{loading
									? 'Loading translations…'
									: translated('General/DebugInfo/Actions/ReloadTranslations', 'Reload translations')}
							</button>
						</section>
					)}

					<div className="debug-info-hint">Alt+I or Esc to close</div>
				</div>
			)}
		</div>
	);
};

export default DebugPanel;
